/**
 * The expert's rubric templates: named sets of criteria they reuse across their own rubrics.
 * Only the signed-in expert's templates are ever listed (the server keys them by expert).
 * Science experts have no rubric templates (no menu entry), so these pages send them home.
 */
import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { ApiError } from '@/api/client'
import { useProfile } from '@/auth/ProfileContext'
import { Dialog } from '@/components/Dialog'
import { RubricEditor, blankCriterion } from '@/components/RubricEditor'
import { useAsync } from '@/components/useAsync'
import { fmtDate } from '@/lib/format'
import { isScienceExpert } from '@/lib/programs'
import {
  countLabel, deleteTemplate, getTemplate, listTemplates, MAX_TEMPLATE_NAME, nameProblem, saveTemplate, templateCriteria, templateProblem,
  TEMPLATES_ROUTE,
} from '@/lib/rubricTemplates'
import type { RubricCriterion, RubricTemplate, RubricTemplateSummary } from '@/lib/types'

export default function RubricTemplates() {
  const { profile } = useProfile()
  const { data, error, reload } = useAsync(listTemplates, [])
  const templates = data?.templates
  const [deleting, setDeleting] = useState<RubricTemplateSummary | null>(null)
  const heading = useRef<HTMLHeadingElement>(null)
  const deleted = useRef(false)
  // The deleted row's button is gone once the list reloads; continue from the page heading.
  useEffect(() => { if (deleted.current && data) { deleted.current = false; heading.current?.focus() } }, [data])
  if (isScienceExpert(profile)) return <Navigate to="/sme" replace />
  const full = !!data && data.templates.length >= data.limit
  return (
    <div className="narrow">
      <div className="page-head">
        <div>
          <h1 ref={heading} tabIndex={-1}>Rubric templates</h1>
          <div className="meta">Sets of criteria you reuse. Add one to a rubric with “Use a template”. Only you can see your templates.</div>
        </div>
        {full ? <span className="muted small">You have {data.limit} templates, the most you can keep. Delete one to create another.</span>
          : <Link className="btn primary" to={`${TEMPLATES_ROUTE}/new`}>+ New template</Link>}
      </div>
      {error && <p className="note err" role="alert">{error} <button className="link small" onClick={reload}>Try again</button></p>}
      {!templates ? !error && <p className="muted" role="status">Loading…</p>
        : templates.length === 0 ? <div className="box subtle">
          <h2>No rubric templates yet</h2>
          <p>Create one here, or write a rubric and choose “Save as template” below its criteria.</p>
        </div>
        : <ul className="template-list">
          {templates.map((t) => <li key={t.template_id}>
            <div>
              <Link to={`${TEMPLATES_ROUTE}/${t.template_id}`}><strong>{t.name}</strong></Link>
              <div className="muted small">{countLabel(t.criteria_count)} · Updated <time dateTime={t.updated_at}>{fmtDate(t.updated_at)}</time></div>
            </div>
            <div className="row">
              <Link className="btn sm" to={`${TEMPLATES_ROUTE}/${t.template_id}`} aria-label={`Edit ${t.name}`}>Edit</Link>
              <button className="sm danger" onClick={() => setDeleting(t)} aria-label={`Delete ${t.name}`}>Delete</button>
            </div>
          </li>)}
        </ul>}
      {deleting && <DeleteTemplate template={deleting} onClose={() => setDeleting(null)}
        onDeleted={() => { deleted.current = true; setDeleting(null); reload() }} />}
    </div>
  )
}

function DeleteTemplate({ template, onClose, onDeleted }: { template: RubricTemplateSummary; onClose: () => void; onDeleted: () => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  async function remove() {
    setBusy(true); setError('')
    try {
      await deleteTemplate(template.template_id)
      onDeleted()
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) { onDeleted(); return }  // already deleted in another tab
      setError((e as Error).message)
      setBusy(false)
    }
  }
  return <Dialog title={`Delete “${template.name}”?`} busy={busy} onClose={onClose}>
    <p>Its {countLabel(template.criteria_count)} are deleted from your templates. Rubrics you already added them to keep their copies.</p>
    {error && <p className="note err" role="alert">{error}</p>}
    <div className="row">
      <button type="button" data-autofocus disabled={busy} onClick={onClose}>Cancel</button>
      <button type="button" className="danger" disabled={busy} onClick={() => void remove()}>{busy ? 'Deleting…' : 'Delete template'}</button>
    </div>
  </Dialog>
}

const starters = () => [blankCriterion(1), blankCriterion(2)]
/** What saving would store, so blank cards and untouched optional fields don't count as changes. */
const snapshot = (name: string, rubric: RubricCriterion[]) => JSON.stringify({ name: name.trim(), criteria: templateCriteria(rubric) })
const asCards = (t: RubricTemplate) => t.criteria.map((c, i) => ({ ...blankCriterion(i + 1), ...c }))

/** `/new` or `/:templateId`; keyed so moving between templates starts afresh. */
export function RubricTemplateEditPage() {
  const { profile } = useProfile()
  const { templateId } = useParams()
  if (isScienceExpert(profile)) return <Navigate to="/sme" replace />
  return <RubricTemplateEdit key={templateId ?? 'new'} templateId={templateId} />
}

function RubricTemplateEdit({ templateId }: { templateId?: string }) {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [rubric, setRubric] = useState<RubricCriterion[]>(starters)
  // The template as loaded or last saved: its updated_at guards against overwriting a save made elsewhere.
  const [loaded, setLoaded] = useState<RubricTemplate | null>(null)
  const [saved, setSaved] = useState(() => snapshot('', starters()))
  const [loadError, setLoadError] = useState('')
  const [attempt, setAttempt] = useState(0)
  const [error, setError] = useState('')
  const [conflict, setConflict] = useState<'gone' | 'changed' | null>(null)
  const [busy, setBusy] = useState(false)
  const [notFound, setNotFound] = useState(false)
  // Bumped whenever a loaded version replaces the criteria, so the editor's Undo history starts afresh.
  const [version, setVersion] = useState(0)
  const mounted = useRef(true)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])
  const ready = !templateId || !!loaded
  const dirty = ready && snapshot(name, rubric) !== saved

  function show(t: RubricTemplate) {
    const cards = asCards(t)
    setName(t.name); setRubric(cards); setSaved(snapshot(t.name, cards)); setLoaded(t); setVersion((n) => n + 1)
  }
  useEffect(() => {
    if (!templateId) return
    let live = true
    setLoadError('')
    getTemplate(templateId).then((t) => { if (live) show(t) }, (e: Error) => {
      if (!live) return
      setLoadError(e.message); setNotFound(e instanceof ApiError && e.status === 404)
    })
    return () => { live = false }
  }, [templateId, attempt])
  // Unsaved edits: warn before closing the tab and before following any link on the page (the
  // sidebar included), also while a save is in flight since it may fail. The app uses
  // <BrowserRouter>, so there is no router blocker; Back and Sign out are not caught.
  useEffect(() => {
    if (!dirty) return
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = '' }
    const follow = (event: MouseEvent) => {
      const link = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
      // A modified or middle click opens a new tab and leaves this page as it is.
      if (!link || link.target === '_blank' || event.defaultPrevented || event.button !== 0
        || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      if (!window.confirm('Leave without saving your changes to this template?')) { event.preventDefault(); event.stopPropagation() }
    }
    window.addEventListener('beforeunload', warn)
    document.addEventListener('click', follow, true)
    return () => { window.removeEventListener('beforeunload', warn); document.removeEventListener('click', follow, true) }
  }, [dirty])

  async function save(target: RubricTemplate | null = loaded) {
    const problem = nameProblem(name) || templateProblem(rubric)
    if (problem) { setError(problem); setBusy(false); return }
    setBusy(true); setError(''); setConflict(null)
    try {
      await saveTemplate(name, templateCriteria(rubric), target ?? undefined)
      if (mounted.current) navigate(TEMPLATES_ROUTE)  // not if the expert already left for another page
    } catch (e) {
      if (!mounted.current) return
      const status = e instanceof ApiError ? e.status : 0
      if (target && status === 404) setConflict('gone')
      else if (target && status === 412) setConflict('changed')
      else setError((e as Error).message)
      setBusy(false)
    }
  }
  /** A failed re-read during conflict recovery: a 404 means the template was deleted meanwhile. */
  function lost(e: unknown) {
    if (e instanceof ApiError && e.status === 404) setConflict('gone')
    else setError((e as Error).message)
  }
  /** After a 412: keep these edits and overwrite the template as it is now. */
  async function overwrite() {
    if (!templateId) return
    setBusy(true)
    try { const current = await getTemplate(templateId); setLoaded(current); await save(current) }
    catch (e) { lost(e); setBusy(false) }
  }
  /** After a 412: drop these edits and show the template as saved elsewhere. */
  async function reloadLatest() {
    if (!templateId || !window.confirm('Discard your changes and load the latest version?')) return
    setBusy(true)
    try { show(await getTemplate(templateId)); setConflict(null) }
    catch (e) { lost(e) }
    finally { setBusy(false) }
  }

  const back = <Link className="backlink" to={TEMPLATES_ROUTE}>← Rubric templates</Link>
  if (loadError) return <div className="narrow">{back}<p className="note err" role="alert">{loadError}{!notFound && <> <button className="link small" onClick={() => setAttempt((n) => n + 1)}>Try again</button></>}</p></div>
  if (!ready) return <div className="narrow">{back}<p className="muted" role="status">Loading…</p></div>
  return (
    <div className="narrow">
      {back}
      <div className="page-head">
        <div>
          <h1>{templateId ? 'Edit rubric template' : 'New rubric template'}</h1>
          <div className="meta">{templateId
            ? 'Changes apply the next time you use the template. Rubrics that already use it keep their copies.'
            : 'A set of criteria you can add to any of your rubrics with “Use a template”.'}</div>
        </div>
      </div>
      <label className="field">
        <span>Template name</span>
        <input type="text" value={name} maxLength={MAX_TEMPLATE_NAME} disabled={busy} placeholder="e.g. Standard drawing checks"
          onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing && dirty && !busy && !conflict) void save() }} />
      </label>
      <section className="form-section">
        <h2>Criteria</h2>
        <p className="muted small">Empty criteria are left out when you save.</p>
        <RubricEditor key={version} value={rubric} onChange={setRubric} readOnly={busy} />
      </section>
      {error && <p className="note err" role="alert">{error}</p>}
      {conflict === 'changed' && <div className="note warn" role="alert">
        <p>This template was changed elsewhere since you opened it. Your edits are still here.</p>
        <div className="row">
          <button className="sm" disabled={busy} onClick={() => void overwrite()}>Save my version over it</button>
          <button className="sm" disabled={busy} onClick={() => void reloadLatest()}>Load the latest version</button>
        </div>
      </div>}
      {conflict === 'gone' && <div className="note warn" role="alert">
        <p>This template was deleted elsewhere. Your edits are still here.</p>
        <button className="sm" disabled={busy} onClick={() => void save(null)}>Save it as a new template</button>
      </div>}
      <div className="row" style={{ marginTop: '1rem' }}>
        <button className="primary" disabled={busy || !dirty || !!conflict} onClick={() => void save()}>{busy ? 'Saving…' : 'Save template'}</button>
        <Link className="btn" to={TEMPLATES_ROUTE}>Cancel</Link>
        {dirty && !busy && <span className="small muted">Unsaved changes</span>}
      </div>
    </div>
  )
}
